import { BoxOfferDetailedEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType, Price } from "@modules/boxOffer/entities/boxOfferTypes"
import { WARNING_OFFER_END_DELAY } from "@modules/boxSubscription/entities/boxSubscriptionConst"
import {
  CouponInputFormik,
  DurationType,
  PauseInputFormik,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import {
  BoxSubscriptionDetailsDto,
  BoxSubscriptionStatus,
} from "@modules/boxSubscription/gateway/boxSubscriptionApiDto"
import { CouponEntity } from "@modules/common/couponObject/entities/couponObjectEntity"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PauseEntity } from "@modules/common/pauseObject/entities/pauseObjectEntity"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { PaymentError } from "@modules/finances/types/stripeErrorTypes"
import { Language } from "@modules/language/utils/languageUtils"
import { LightPromoCodeEntity } from "@modules/promoCode/entities/lightPromoCodeEntity"
import dayjs from "dayjs"
import { DefaultTheme } from "styled-components"

type BoxSubscriptionDetailsState = Omit<
  BoxSubscriptionDetailsEntity,
  | "getWordingStatus"
  | "getColorStatus"
  | "getWordingCouponDate"
  | "getWordingCouponPriceAfterDiscount"
  | "isDisplayWarning"
  | "getWordingWarning"
  | "getCoupon"
  | "getPause"
  | "getWordingPause"
>

export class BoxSubscriptionDetailsEntity {
  id: string
  type: BoxOfferType
  creationDate: Date
  status: BoxSubscriptionStatus
  periodEndDate: Date | null
  minimumCommitmentDate: Date | null
  minimumCommitmentPeriod: string | null
  errors: PaymentError | null
  pause: PauseEntity | null
  coupon: CouponEntity | null
  referencePrice: Price | null
  priceObject: Price | null
  subscriptionDate: Date
  subscriptionEndDate: Date | null
  deferredDate: Date | null
  usedPromoCode: LightPromoCodeEntity | null
  offer: BoxOfferDetailedEntity

  private constructor(boxSubcription: BoxSubscriptionDetailsState) {
    this.id = boxSubcription.id
    this.type = boxSubcription.type
    this.creationDate = boxSubcription.creationDate
    this.status = boxSubcription.status
    this.periodEndDate = boxSubcription.periodEndDate
    this.minimumCommitmentDate = boxSubcription.minimumCommitmentDate
    this.minimumCommitmentPeriod = boxSubcription.minimumCommitmentPeriod
    this.errors = boxSubcription.errors
    this.pause = boxSubcription.pause
    this.coupon = boxSubcription.coupon
    this.referencePrice = boxSubcription.referencePrice
    this.priceObject = boxSubcription.priceObject
    this.subscriptionDate = boxSubcription.subscriptionDate
    this.subscriptionEndDate = boxSubcription.subscriptionEndDate
    this.deferredDate = boxSubcription.deferredDate
    this.usedPromoCode = boxSubcription.usedPromoCode
    this.offer = boxSubcription.offer
  }

  static fromDto(boxSubcriptionDto: BoxSubscriptionDetailsDto): BoxSubscriptionDetailsEntity {
    return new BoxSubscriptionDetailsEntity({
      id: boxSubcriptionDto.id,
      type: boxSubcriptionDto.type,
      creationDate: new Date(boxSubcriptionDto.creationDate),
      status: boxSubcriptionDto.status,
      periodEndDate: boxSubcriptionDto.periodEndDate ? new Date(boxSubcriptionDto.periodEndDate) : null,
      minimumCommitmentDate: boxSubcriptionDto.minimumCommitmentDate
        ? new Date(boxSubcriptionDto.minimumCommitmentDate)
        : null,
      minimumCommitmentPeriod: boxSubcriptionDto.minimumCommitmentPeriod,
      errors: boxSubcriptionDto.errors,
      pause: boxSubcriptionDto.pause ? PauseEntity.fromDto(boxSubcriptionDto.pause) : null,
      coupon: boxSubcriptionDto.coupon ? CouponEntity.fromDto(boxSubcriptionDto.coupon) : null,
      referencePrice: boxSubcriptionDto.referencePrice,
      priceObject: boxSubcriptionDto.priceObject,
      subscriptionDate: new Date(boxSubcriptionDto.subscriptionDate),
      subscriptionEndDate: boxSubcriptionDto.subscriptionEndDate
        ? new Date(boxSubcriptionDto.subscriptionEndDate)
        : null,
      deferredDate: boxSubcriptionDto.deferredDate ? new Date(boxSubcriptionDto.deferredDate) : null,
      usedPromoCode: boxSubcriptionDto.usedPromoCode
        ? LightPromoCodeEntity.fromDto(boxSubcriptionDto.usedPromoCode)
        : null,
      offer: BoxOfferDetailedEntity.fromDto(boxSubcriptionDto.offer),
    })
  }

  getCoupon(): CouponInputFormik {
    if (!this.coupon) {
      return {
        type: CouponType.PERCENT,
        value: 0,
        durationType: DurationType.UNLIMITED,
        months: 0,
      }
    }
    return {
      type: this.coupon.type,
      value: this.coupon.value,
      durationType: this.coupon.duration ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: this.coupon.duration || null,
    }
  }

  getPause(): PauseInputFormik {
    if (!this.pause) {
      return {
        durationType: DurationType.MONTHLY,
        months: 1,
      }
    }

    if (this.pause.resumeAt === null && this.pause.pausedAt) {
      return {
        durationType: DurationType.UNLIMITED,
        months: 0,
      }
    }

    const pausedAt = dayjs(this.pause.pausedAt)
    const resumeAt = dayjs(this.pause.resumeAt)
    const months = resumeAt.diff(pausedAt, "month")

    return {
      durationType: months > 0 ? DurationType.MONTHLY : DurationType.UNLIMITED,
      months: months > 0 ? months : 1,
    }
  }

  getWordingStatus(): string {
    switch (this.status) {
      case BoxSubscriptionStatus.ACTIVE:
      case BoxSubscriptionStatus.NEW_BILLING_DATE:
        return word("box.members.tab.offer.status.ongoing")
      case BoxSubscriptionStatus.PENDING_PAYMENT:
        return word("box.members.tab.offer.status.pendingPayment") // Should not be used yet as this status means that the athlete is a lead
      case BoxSubscriptionStatus.PAUSE:
        return word("box.members.tab.offer.status.suspended")
      case BoxSubscriptionStatus.INACTIVE:
        return word("box.members.tab.offer.status.expired")
      case BoxSubscriptionStatus.PAYMENT_IN_ERROR:
        return word("box.members.tab.offer.status.paymentError")
      case BoxSubscriptionStatus.PENDING_CANCELLATION:
        return word("box.members.tab.offer.status.pendingCancellation")
      case BoxSubscriptionStatus.SCHEDULED_ACTIVATION:
        return word("box.members.tab.offer.status.delayedStart")
      case BoxSubscriptionStatus.PROCESSING_PAYMENT:
        return word("box.members.tab.offer.status.processingPayment")
      default:
        return ""
    }
  }

  getColorStatus(theme: DefaultTheme): string {
    switch (this.status) {
      case BoxSubscriptionStatus.ACTIVE:
      case BoxSubscriptionStatus.NEW_BILLING_DATE:
      case BoxSubscriptionStatus.SCHEDULED_ACTIVATION:
        return theme.sucess
      case BoxSubscriptionStatus.PROCESSING_PAYMENT:
      case BoxSubscriptionStatus.PENDING_PAYMENT:
      case BoxSubscriptionStatus.PAUSE:
        return theme.warning
      case BoxSubscriptionStatus.INACTIVE:
      case BoxSubscriptionStatus.PAYMENT_IN_ERROR:
      case BoxSubscriptionStatus.PENDING_CANCELLATION:
        return theme.error
      default:
        return theme.error
    }
  }

  getWordingCouponDate(): string {
    if (!this.coupon) return ""

    return this.coupon.getWordingDate()
  }

  // The coupon is calculated after calculating the promo code if there is one
  getWordingCouponPriceAfterDiscount(language: Language): string {
    if (!this.priceObject || !this.referencePrice || !this.coupon) {
      return ""
    }

    let price = this.referencePrice.value
    if (this.usedPromoCode !== null) {
      price =
        this.usedPromoCode.type === CouponType.PERCENT
          ? price - (price * this.usedPromoCode.value) / 100
          : price - this.usedPromoCode.value
    }

    price =
      this.coupon.type === CouponType.PERCENT ? price - (price * this.coupon.value) / 100 : price - this.coupon.value

    return formatCurrency(price, this.priceObject.currency, language)
  }

  isDisplayWarning(): boolean {
    if (
      this.status === BoxSubscriptionStatus.PENDING_CANCELLATION &&
      this.subscriptionEndDate !== null &&
      dayjs(this.subscriptionEndDate).diff(dayjs(), "day") <= WARNING_OFFER_END_DELAY
    )
      return true

    return false
  }

  getWordingWarning(): string {
    if (this.subscriptionEndDate === null) return ""

    return word("box.member.table.offer.item.status.ends", {
      count: dayjs(this.subscriptionEndDate).startOf("day").diff(dayjs().startOf("day"), "day"),
    })
  }

  getWordingPause(): string {
    if (!this.pause) return ""
    if (!this.pause.resumeAt) {
      return `${word("box.member.table.offer.resume.unlimitedDuration")} (${word(
        "box.member.table.offer.item.status.paused.since",
        {
          date: dayjs(this.pause.pausedAt).format("L"),
        },
      )})`
    } else {
      return word("box.member.table.offer.item.status.paused.range", {
        pausedDate: dayjs(this.pause.pausedAt).format("L"),
        resumeDate: dayjs(this.pause.resumeAt).format("L"),
      })
    }
  }
}
